/**
 * Recetas estándar de la organización (RES-45, épica D3).
 *
 * La receta estándar se identifica por el nombre del producto normalizado igual que el backend
 * (`services/inventory/shared/orgRecipes.js`): sin tildes, minúsculas y espacios colapsados. Así
 * "Pizza  Muzzarella" y "pizza muzzarella" son la misma receta en todas las sucursales.
 */
import { normalizeUnit } from './units';

/** Estados de un producto frente a la receta estándar, en el orden en que se muestran. */
export const ORG_RECIPE_STATUSES = [
  'applied',
  'pending',
  'up_to_date',
  'local',
  'own_recipe',
  'combo',
  'conflict',
];

/**
 * Nombre normalizado (misma regla que la clave `recipeKey` del backend).
 * @param {string} name - Nombre del producto
 * @returns {string} Normalizado
 */
export function normalizeRecipeName(name) {
  return String(name || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/**
 * Receta estándar que corresponde a un producto de la carta: primero por `recipeOrgKey` (la que
 * sigue) y si no, por nombre normalizado.
 * @param {Array<{recipeKey: string, itemName: string}>} orgRecipes - Recetas estándar
 * @param {{name?: string, recipeOrgKey?: string}} product - Producto
 * @returns {Object|null} Receta estándar o null
 */
export function findOrgRecipeForItem(orgRecipes, product) {
  if (!Array.isArray(orgRecipes) || orgRecipes.length === 0 || !product) return null;
  if (product.recipeOrgKey) {
    const byKey = orgRecipes.find((r) => r.recipeKey === product.recipeOrgKey);
    if (byKey) return byKey;
  }
  const key = normalizeRecipeName(product.name);
  if (!key) return null;
  return orgRecipes.find((r) => (r.recipeKey || normalizeRecipeName(r.itemName)) === key) || null;
}

/**
 * Líneas de receta tal como las espera la API: sin `name` ni `cost` (se muestran en pantalla
 * pero los resuelve el backend) y con la cantidad numérica.
 * @param {Array} lines - Líneas del editor
 * @returns {Array<{inventoryItemId: string, quantity: number, unit: string, isCritical: boolean}>}
 */
export function toRecipePayloadLines(lines) {
  return (Array.isArray(lines) ? lines : []).map((line) => ({
    inventoryItemId: line.inventoryItemId,
    quantity: Number(line.quantity),
    unit: normalizeUnit(line.unit) || line.unit,
    isCritical: line.isCritical === true,
  }));
}

/**
 * Líneas de una receta estándar con el nombre del insumo para mostrarlas en el editor.
 * @param {Array} lines - Líneas de la API
 * @param {Array<{itemId: string, name: string}>} inventoryItems - Insumos conocidos
 * @returns {Array} Líneas con `name`
 */
export function withIngredientNames(lines, inventoryItems = []) {
  return (Array.isArray(lines) ? lines : []).map((line) => ({
    ...line,
    name:
      line.name ||
      (inventoryItems || []).find((i) => i.itemId === line.inventoryItemId)?.name ||
      line.inventoryItemId,
  }));
}

/**
 * Body de `saveOrgRecipe`. `expectedVersion` es la versión que se editó (0 = receta nueva): si
 * otro la guardó antes, el backend responde 409 `VERSION_CONFLICT` en vez de pisarla.
 * @param {{itemName: string, lines: Array, version?: number, apply?: boolean}} draft - Editor
 * @returns {Object} Body
 */
export function buildOrgRecipePayload({ itemName, lines, version, apply = true }) {
  return {
    itemName: String(itemName || '')
      .trim()
      .replace(/\s+/g, ' '),
    recipe: toRecipePayloadLines(lines),
    expectedVersion: Number(version) > 0 ? Number(version) : 0,
    apply: apply !== false,
  };
}

/**
 * Nombres de productos de la carta (sin combos, sin borrados, sin repetir por nombre normalizado)
 * para sugerir al crear una receta estándar.
 * @param {Array<{items?: Array}>} menu - Categorías de `getMenu()`
 * @returns {string[]} Nombres ordenados
 */
export function menuProductNames(menu) {
  const seen = new Map();
  (Array.isArray(menu) ? menu : [])
    .filter((cat) => cat && cat.status !== 'deleted')
    .forEach((cat) => {
      (cat.items || []).forEach((item) => {
        if (!item || item.status === 'deleted' || item.isCombo || !item.name) return;
        const key = normalizeRecipeName(item.name);
        if (key && !seen.has(key)) seen.set(key, item.name.trim());
      });
    });
  return [...seen.values()].sort((a, b) => a.localeCompare(b, 'es'));
}

/**
 * Aplicaciones agrupadas por local, con los locales ordenados por nombre.
 * @param {Array<{restaurantId: string, restaurantName?: string}>} applications - Aplicaciones
 * @returns {Array<{restaurantId: string, restaurantName: string, items: Array}>} Grupos
 */
export function groupApplicationsByRestaurant(applications) {
  const groups = new Map();
  (Array.isArray(applications) ? applications : []).forEach((a) => {
    if (!groups.has(a.restaurantId)) {
      groups.set(a.restaurantId, {
        restaurantId: a.restaurantId,
        restaurantName: a.restaurantName || a.restaurantId,
        items: [],
      });
    }
    groups.get(a.restaurantId).items.push(a);
  });
  return [...groups.values()].sort((a, b) =>
    String(a.restaurantName).localeCompare(String(b.restaurantName), 'es')
  );
}

/**
 * Estados con al menos un producto, en el orden de `ORG_RECIPE_STATUSES`.
 * @param {{byStatus?: Object}} summary - Resumen del backend
 * @returns {Array<{status: string, count: number}>} Estados con cantidad
 */
export function nonZeroStatuses(summary) {
  const byStatus = (summary && summary.byStatus) || {};
  return ORG_RECIPE_STATUSES.filter((s) => Number(byStatus[s]) > 0).map((status) => ({
    status,
    count: Number(byStatus[status]),
  }));
}
